import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import {
    Straighten as MeasurementIcon,
    DragIndicator as DragIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    Restore as RestoreIcon,
    VisibilityOff as HiddenIcon,
    DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
// @ts-ignore — untyped JS design tokens (TODO(ts-migration): convert coachTokens.js)
import {t} from "../../dashboard/coach/coachTokens";
import {unitLabel} from "../../measurements/measurementUnits";
import type {TraineeMeasurementItem} from "../../../services/TraineeMeasurementService";
import {imageUrl} from "../../../utils/imageUrl";

interface Props {
    item: TraineeMeasurementItem;
    index: number;
    /**
     * Why this measurement cannot be hidden for this trainee, in Hebrew — set when a COMPUTED
     * measurement they can still see derives its value from it. `null`/absent = hiding is allowed.
     */
    lockReason?: string | null;
    onEdit: () => void;
    onHide: () => void;
    onUnhide: () => void;
    onDelete: () => void;
}

/**
 * One draggable card in a trainee's measurement grid.
 *
 * Mirrors the company-level per-gender grid (MeasurementDefinitionsScreen's
 * SortableMeasurementCard): same two-row card shape — drag badge + image + name on top, unit +
 * hover actions below — so the trainer sees one consistent way to arrange measurements whether
 * they're editing the company catalog or one person's list.
 *
 * Three states, and the actions differ per state:
 *   - HIDDEN   → greyed + dashed, and the ONLY action is un-hide. Still draggable, so a hidden
 *                measurement keeps its place in the order and comes back where it was.
 *   - PERSONAL → belongs to this trainee alone: edit, hide, and permanently delete.
 *   - COMPANY  → hide only. Editing a company measurement belongs in the company catalog, where
 *                it affects everyone.
 *
 * Every nested button needs `onPointerDown` stopPropagation: dnd-kit's PointerSensor claims the
 * gesture after 5px and would otherwise swallow the click — which presents as "the button does
 * nothing" with no console error.
 */
const SortableMeasurementRow = ({item, index, lockReason, onEdit, onHide, onUnhide, onDelete}: Props) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
        useSortable({id: item.id as number});

    const hidden = item.hidden === true;
    const personal = item.personal === true;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : hidden ? 0.55 : 1,
        zIndex: isDragging ? 2 : "auto" as const,
    };

    const stop = (e: React.PointerEvent) => e.stopPropagation();

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            data-testid={`measurement-row-${item.id}`}
            sx={{
                position: "relative",
                display: "flex", flexDirection: "column", gap: "8px",
                padding: "10px 12px", borderRadius: t.rs,
                background: hidden ? t.bg : t.white,
                border: hidden
                    ? `1px dashed ${t.border}`
                    : `1px solid ${isDragging ? t.green : t.border}`,
                boxShadow: isDragging ? "0 8px 20px rgba(1,187,167,.22)" : "none",
                cursor: "grab", touchAction: "none", userSelect: "none",
                "&:hover": {borderColor: hidden ? t.t3 : t.green},
                "&:hover .rowActions": {opacity: 1},
            }}
        >
            {/* Header: drag badge + image + name + badges */}
            <Box sx={{display: "flex", alignItems: "center", gap: "7px", minWidth: 0}}>
                <Box sx={{display: "inline-flex", alignItems: "center", color: t.t3, flexShrink: 0}}>
                    <DragIcon sx={{fontSize: 15}}/>
                    <Typography sx={{fontSize: "10px", fontWeight: 700, ml: "1px"}}>{index + 1}</Typography>
                </Box>

                <Box sx={{
                    width: 26, height: 26, borderRadius: t.rxs, background: t.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, overflow: "hidden",
                }}>
                    {item.imageUrl
                        ? <img src={imageUrl(item.imageUrl, 26) ?? undefined} alt={item.nameHeb || item.name}
                               style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                        : <MeasurementIcon sx={{fontSize: 15, color: t.t2}}/>}
                </Box>

                <Typography sx={{
                    fontSize: "12px", fontWeight: 600, color: hidden ? t.t2 : t.text,
                    minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    flex: 1,
                }}>
                    {item.nameHeb || item.name}
                </Typography>

                {personal && (
                    <Tooltip title="מדד אישי — קיים רק אצל מתאמן זה">
                        <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: "3px",
                            height: 18, px: "6px", borderRadius: t.rxs,
                            background: t.greenLight, color: t.greenText,
                            fontSize: "10px", fontWeight: 700, flexShrink: 0,
                        }}>
                            <PersonIcon sx={{fontSize: 11}}/>
                            אישי
                        </Box>
                    </Tooltip>
                )}

                {item.computed === true && (
                    <Tooltip title="מדד מחושב — הערך מחושב אוטומטית ואינו ניתן להזנה">
                        <Box sx={{
                            display: "inline-flex", alignItems: "center",
                            height: 18, px: "6px", borderRadius: t.rxs,
                            background: t.bg, color: t.t2,
                            fontSize: "10px", fontWeight: 700, flexShrink: 0,
                        }}>
                            מחושב
                        </Box>
                    </Tooltip>
                )}
            </Box>

            {/* Footer: unit (+ hidden badge) + actions. A hidden card offers ONLY un-hide. */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "5px", minWidth: 0}}>
                    <Typography sx={{fontSize: "11px", color: t.t3}}>
                        {unitLabel(item.unit)}
                    </Typography>
                    {hidden && (
                        <Box sx={{
                            display: "inline-flex", alignItems: "center",
                            height: 17, px: "5px", borderRadius: t.rxs,
                            background: t.border, color: t.t2,
                            fontSize: "10px", fontWeight: 700, flexShrink: 0,
                        }}>
                            מוסתר
                        </Box>
                    )}
                </Box>

                <Box className="rowActions" sx={{
                    display: "flex", alignItems: "center", gap: "4px", flexShrink: 0,
                    opacity: hidden ? 1 : 0, transition: "opacity .12s",
                }}>
                    {hidden ? (
                        <Tooltip title="הצג מדד זה שוב למתאמן">
                            <IconButton
                                size="small"
                                aria-label="הצג"
                                onPointerDown={stop}
                                onClick={(e) => { e.stopPropagation(); onUnhide(); }}
                                sx={{p: "3px", color: t.green, "&:hover": {background: t.greenLight}}}
                            >
                                <RestoreIcon sx={{fontSize: 15}}/>
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <>
                            {personal && (
                                <Tooltip title="ערוך מדד אישי">
                                    <IconButton
                                        size="small"
                                        aria-label="ערוך"
                                        onPointerDown={stop}
                                        onClick={(e) => { e.stopPropagation(); onEdit(); }}
                                        sx={{p: "3px", color: t.green, "&:hover": {background: t.greenLight}}}
                                    >
                                        <EditIcon sx={{fontSize: 15}}/>
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* The <span> is required: MUI's Tooltip needs an enabled child to
                                receive hover, and the explanation is why the button is disabled. */}
                            <Tooltip title={lockReason || "הסתר מדד זה למתאמן — הנתונים שנרשמו יישמרו"}>
                                {/* The aria-label is what keeps this button named now that the
                                    Tooltip's <span> would otherwise claim the accessible name. */}
                                <span>
                                <IconButton
                                    size="small"
                                    aria-label="הסתר"
                                    disabled={!!lockReason}
                                    onPointerDown={stop}
                                    onClick={(e) => { e.stopPropagation(); onHide(); }}
                                    sx={{p: "3px", color: t.t2, "&:hover": {background: t.bg}}}
                                >
                                    <HiddenIcon sx={{fontSize: 15}}/>
                                </IconButton>
                                </span>
                            </Tooltip>

                            {personal && (
                                <Tooltip title="מחק לצמיתות — המדד וכל הנתונים שלו יימחקו">
                                    <IconButton
                                        size="small"
                                        aria-label="מחק"
                                        onPointerDown={stop}
                                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                        sx={{p: "3px", color: t.red, "&:hover": {background: t.redLight}}}
                                    >
                                        <DeleteForeverIcon sx={{fontSize: 15}}/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default SortableMeasurementRow;
